import type {
  Plan,
  PlanGroup,
  PlanType,
  PlanInterval,
} from '../../../../types';

type Options = {
  // Ensures the returned plan will be this billing interval.
  nextPlanInterval?: PlanInterval;
  // Ensures the returned plan will be no lower than this type.
  nextPlanType?: PlanType;
};
/**
 * Returns the next plan up from the account's current plan based on plan type and Task usage.
 * For example, if the account has used 800 Tasks, this function would return the Starter
 * 1500 plan.
 *
 * This function assumes all plans have already been sorted by Task limit from lowest to highest
 * within their respective plan stacks.
 *
 * Refer to https://gitlab.com/zapier/zapier/-/blob/main/graphql-proxy/src/structures/Plan/utils/getNextPlanV2/index.ts#L18
 */
export const getNextPlan = (
  planStacks: Array<PlanGroup>,
  currentPlan: {
    actions_used: number;
    interval: PlanInterval;
    plan_type: PlanType;
    total_actions: number;
  },
  options: Options = {}
) => {
  const nextPlanInterval =
    options.nextPlanInterval ?? currentPlan.interval ?? 'year';

  const nextPlanType = options.nextPlanType ?? currentPlan.plan_type ?? 'Free';

  const firstEligiblePlanTypeIndex =
    planStacks.findIndex((planStack) => planStack.planType === nextPlanType) ??
    0;

  let nextPlan: Plan | undefined;

  // Check each plan stack for a suitable plan, starting from the first eligible plan type.
  for (
    let planTypeIndex = firstEligiblePlanTypeIndex;
    planTypeIndex < planStacks.length && !nextPlan;
    planTypeIndex++
  ) {
    nextPlan = planStacks[planTypeIndex]?.plans?.find(
      (plan) =>
        plan.actions > currentPlan.total_actions &&
        plan.actions > currentPlan.actions_used &&
        plan.interval === nextPlanInterval
    );
  }

  return nextPlan;
};
